import { LogLevel } from "@azure/msal-browser";
import type { Configuration } from "@azure/msal-browser";
import {
  AZURE_CLIENT_ID,
  AZURE_REDIRECT_URI,
  AZURE_TENANT_ID,
} from "@/lib/env";

const authorityOf = (tenantId: string) =>
  `https://login.microsoftonline.com/${tenantId}`;

export const msalConfig: Configuration = {
  auth: {
    // The Client ID of the registered application
    clientId: AZURE_CLIENT_ID,

    // Delivery is multi-tenant: learners sign in from other tenants
    authority: authorityOf("common"),

    // The URI to redirect to after successful authentication
    redirectUri: AZURE_REDIRECT_URI || window.location.origin,

    // The URI to redirect to after logout
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    // Stores tokens securely in local storage, surviving reloads and tab closes
    cacheLocation: "localStorage",
  },
  system: {
    // We send the ID token to our backend, but MSAL only ever decides that a
    // token needs renewing by looking at the ACCESS token — it hands back the
    // cached ID token unchecked. Entra gives ID tokens a fixed 60-minute
    // lifetime and access tokens a randomised 60-90 minute one, so once the
    // two drift apart MSAL reports a healthy cache while the ID token it
    // returns is already expired, and the backend answers 401.
    //
    // Renewing the access token 35 minutes early closes that gap: the worst
    // case (a 90-minute access token) renews at minute 55, still inside the ID
    // token's 60 minutes, and every renewal issues a fresh ID token alongside.
    // Keep this above 30 minutes (the largest possible gap) and below 60 (the
    // shortest access token lifetime, past which every call would skip the
    // cache and hit the network). See authConfig.test.ts.
    tokenRenewalOffsetSeconds: 35 * 60,
    loggerOptions: {
      loggerCallback: (
        level: LogLevel,
        message: string,
        containsPii: boolean
      ) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
    },
  },
};

// The API scopes you want the user to consent to
export const loginRequest = {
  scopes: ["openid", "profile", "email"].filter(Boolean),
};

// Preview pages sign in against the internal tenant only (see lib/previewTenant).
export const previewLoginRequest = {
  ...loginRequest,
  authority: authorityOf(AZURE_TENANT_ID),
};
